/* eslint-disable @typescript-eslint/naming-convention --
 * "ZIndex" contains consecutive capitals, which strictPascalCase/strictCamelCase
 * reject. The spelling is kept deliberately: it matches Kendo's own
 * `kCurrentZIndex` injection key and the `PopupZIndexProvider` name this module
 * was extracted from in Window.vue.
 */
import { defineComponent, provide } from 'vue';

/**
 * Kendo's own default for dialogs/windows. Kendo hardcodes this as the value it
 * provides to descendant popups:
 *   `provide: () => ({ kCurrentZIndex: 10002 })`
 * so every popup inside a Window/Dialog renders at 10003, regardless of where
 * that Window actually sits in the stack.
 */
export const DEFAULT_DIALOGS_ZINDEX = 10002;

/**
 * Re-provides Kendo's `kCurrentZIndex` for popups rendered inside a Window or Dialog.
 *
 * Kendo's Window provides a fixed `kCurrentZIndex` (DEFAULT_DIALOGS_ZINDEX = 10002)
 * to descendant popups, which then use `kCurrentZIndex + 1` (= 10003) as their own
 * z-index. But this app manages window z-index itself via WindowManager, and windows
 * routinely climb above 10003 (the Patient window seeds the stack at 10002 and
 * `bringWindowToFront` adds +2 each time; `onEditAlert` even sets 10009). A popup at
 * 10003 then renders *behind* its own window - e.g. the Alert Type dropdown opening
 * behind the Add Alert modal, or the date/duration pickers opening behind the
 * Create Shift modal.
 *
 * This provider must sit INSIDE Kendo's Window slot so it is the nearest provider for
 * the slotted content (verified: the outer component's provide is shadowed by Kendo's
 * own provide otherwise). It provides an object whose `valueOf()` returns the window's
 * current z-index, because Kendo reads the value as a plain number (`+ 1`) at popup
 * render time - so popups always land just above their window, even after the window
 * is brought to the front and its z-index changes.
 */
export const PopupZIndexProvider = defineComponent({
  name: 'PopupZIndexProvider',
  props: { zIndex: { type: Number, required: true } },
  setup(providerProps, { slots }) {
    provide('kCurrentZIndex', { valueOf: () => providerProps.zIndex });
    return () => slots.default?.();
  },
});

/**
 * Resolves the effective z-index for an element by walking up to the nearest
 * ancestor that declares a numeric z-index.
 *
 * Needed because Kendo may place the z-index on the Window's outer wrapper rather
 * than on `.k-window` itself, and because components like Modal.vue do not know
 * their own z-index (Kendo assigns it, no explicit prop is passed). Falls back to
 * Kendo's default so behaviour is unchanged when nothing in the chain sets one.
 */
export function resolveStackingZIndex(element: HTMLElement | null | undefined): number {
  let node: HTMLElement | null = element ?? null;

  while (node) {
    const parsed = Number.parseInt(window.getComputedStyle(node).zIndex, 10);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
    node = node.parentElement;
  }

  return DEFAULT_DIALOGS_ZINDEX;
}
